import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, CardActionArea, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
  Snackbar, IconButton, Chip, Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  AttachMoney as PreciosIcon, Help as FaqIcon, Build as ServiciosIcon,
  Description as DefaultFileIcon, Save as SaveIcon, Close as CloseIcon,
  Refresh as RefreshIcon, Info as InfoIcon, Visibility as PreviewIcon,
  Edit as EditIcon, Schedule as ScheduleIcon, Storage as StorageIcon,
  LocalOffer as OfertasIcon, Security as GarantiaIcon, ContactPhone as ContactoIcon,
  Map as ZonasIcon, SupportAgent as SoporteIcon, EmojiObjects as ObjecionesIcon,
  Route as RutasIcon, MenuBook as CompletoIcon, EmojiEvents as CasosIcon,
  CompareArrows as CompetenciaIcon, Engineering as InstalacionIcon,
  CloudSync as ReindexIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const FILE_ICON_MAP: Record<string, React.ReactNode> = {
  precios: <PreciosIcon sx={{ fontSize: 40 }} />,
  faq: <FaqIcon sx={{ fontSize: 40 }} />,
  servicios: <ServiciosIcon sx={{ fontSize: 40 }} />,
  ofertas: <OfertasIcon sx={{ fontSize: 40 }} />,
  garantia: <GarantiaIcon sx={{ fontSize: 40 }} />,
  contacto: <ContactoIcon sx={{ fontSize: 40 }} />,
  zonas: <ZonasIcon sx={{ fontSize: 40 }} />,
  'soporte-tecnico': <SoporteIcon sx={{ fontSize: 40 }} />,
  soporte: <SoporteIcon sx={{ fontSize: 40 }} />,
  objeciones: <ObjecionesIcon sx={{ fontSize: 40 }} />,
  rutas: <RutasIcon sx={{ fontSize: 40 }} />,
  'driports-completo': <CompletoIcon sx={{ fontSize: 40 }} />,
  'casos-exito': <CasosIcon sx={{ fontSize: 40 }} />,
  competencia: <CompetenciaIcon sx={{ fontSize: 40 }} />,
  instalacion: <InstalacionIcon sx={{ fontSize: 40 }} />,
};

const FILE_COLOR_MAP: Record<string, string> = {
  precios: '#2e7d32',
  faq: '#1565c0',
  servicios: '#e65100',
  ofertas: '#6a1b9a',
  garantia: '#c62828',
  contacto: '#00838f',
  zonas: '#4e342e',
  'soporte-tecnico': '#0277bd',
  soporte: '#0277bd',
  objeciones: '#f57f17',
  rutas: '#2e7d32',
  'driports-completo': '#283593',
  'casos-exito': '#00695c',
  competencia: '#d84315',
  instalacion: '#4527a0',
};

function getFileIcon(name: string): React.ReactNode {
  const base = name.replace('.md', '').toLowerCase();
  return FILE_ICON_MAP[base] || <DefaultFileIcon sx={{ fontSize: 40 }} />;
}

function getFileColor(name: string): string {
  const base = name.replace('.md', '').toLowerCase();
  return FILE_COLOR_MAP[base] || '#546e7a';
}

function titleCase(name: string): string {
  return name
    .replace('.md', '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatDate(date: string | Date): string {
  if (!date) return '-';
  return new Date(date).toLocaleString('es-DO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const KnowledgeBasePage: React.FC = () => {
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastModified, setLastModified] = useState<string>('');
  const [previewMode, setPreviewMode] = useState(false);

  // Re-index
  const [reindexing, setReindexing] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success',
  });

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getBotKnowledge();
      setFiles(res.data || []);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Error al cargar archivos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const openEditor = useCallback(async (filename: string) => {
    setEditorOpen(true);
    setEditingFile(filename);
    setFileLoading(true);
    setPreviewMode(false);
    try {
      const res = await apiService.getBotKnowledgeFile(filename);
      const content = res.data?.content || '';
      setFileContent(content);
      setOriginalContent(content);
      setLastModified(res.data?.lastModified || '');
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Error al cargar el archivo',
        severity: 'error',
      });
      setEditorOpen(false);
    } finally {
      setFileLoading(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingFile) return;
    setSaving(true);
    try {
      await apiService.updateBotKnowledgeFile(editingFile, fileContent);
      setOriginalContent(fileContent);
      setSnackbar({ open: true, message: 'Archivo guardado exitosamente', severity: 'success' });
      fetchFiles();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Error al guardar el archivo',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [editingFile, fileContent, fetchFiles]);

  const handleCloseEditor = useCallback(() => {
    if (fileContent !== originalContent) {
      if (!window.confirm('Tienes cambios sin guardar. ¿Deseas salir?')) return;
    }
    setEditorOpen(false);
    setEditingFile(null);
    setFileContent('');
    setOriginalContent('');
    setPreviewMode(false);
  }, [fileContent, originalContent]);

  const hasChanges = fileContent !== originalContent;

  const handleReindex = useCallback(async () => {
    setReindexing(true);
    try {
      await apiService.reindexKnowledgeBase();
      setSnackbar({ open: true, message: 'Re-indexacion iniciada. El bot usara el contenido actualizado en unos minutos.', severity: 'info' });
    } catch (e: any) {
      setSnackbar({ open: true, message: 'Error al re-indexar: ' + (e.response?.data?.message || e.message), severity: 'error' });
    } finally {
      setTimeout(() => setReindexing(false), 3000);
    }
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Base de Conocimiento</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Re-indexar en Qdrant (actualiza lo que el bot sabe)">
            <Button
              variant="outlined"
              startIcon={reindexing ? <CircularProgress size={18} /> : <ReindexIcon />}
              onClick={handleReindex}
              disabled={reindexing}
              size="small"
            >
              {reindexing ? 'Indexando...' : 'Re-indexar RAG'}
            </Button>
          </Tooltip>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchFiles} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        Estos archivos alimentan la IA del bot. Despues de editar, presiona <strong>Re-indexar RAG</strong> para que el bot use el contenido actualizado.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      )}

      {!loading && (
        <Grid container spacing={3}>
          {files.map((file) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={file.name}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea onClick={() => openEditor(file.name)} sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Box
                      sx={{
                        backgroundColor: getFileColor(file.name),
                        color: 'white',
                        borderRadius: 3,
                        width: 72,
                        height: 72,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {getFileIcon(file.name)}
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {titleCase(file.name)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        icon={<StorageIcon />}
                        label={formatFileSize(file.size || 0)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {file.lastModified && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(file.lastModified)}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
          {files.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No se encontraron archivos de conocimiento</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Fullscreen Editor Dialog */}
      <Dialog open={editorOpen} onClose={handleCloseEditor} fullScreen>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {editingFile && getFileIcon(editingFile)}
              <Typography variant="h6" component="span">
                {editingFile ? titleCase(editingFile) : ''}
              </Typography>
              {hasChanges && (
                <Chip label="Sin guardar" color="warning" size="small" />
              )}
            </Box>
            {lastModified && (
              <Typography variant="caption" color="text.secondary">
                Ultimo cambio: {formatDate(lastModified)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={previewMode ? 'Editar' : 'Vista previa'}>
              <IconButton onClick={() => setPreviewMode(!previewMode)} color={previewMode ? 'primary' : 'default'}>
                {previewMode ? <EditIcon /> : <PreviewIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseEditor}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {fileLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : previewMode ? (
            <Box sx={{ flex: 1, p: 3, overflow: 'auto', backgroundColor: 'grey.50' }}>
              <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
                <Box
                  sx={{
                    fontFamily: 'inherit',
                    '& h1': { fontSize: '1.8rem', fontWeight: 700, mb: 2, mt: 1 },
                    '& h2': { fontSize: '1.4rem', fontWeight: 600, mb: 1.5, mt: 2 },
                    '& h3': { fontSize: '1.1rem', fontWeight: 600, mb: 1, mt: 1.5 },
                    '& p': { mb: 1, lineHeight: 1.7 },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& li': { mb: 0.5 },
                  }}
                >
                  {fileContent.split('\n').map((line, i) => {
                    if (line.startsWith('### ')) return <Typography key={i} variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>{line.slice(4)}</Typography>;
                    if (line.startsWith('## ')) return <Typography key={i} variant="h5" sx={{ fontWeight: 600, mt: 2.5, mb: 1.5 }}>{line.slice(3)}</Typography>;
                    if (line.startsWith('# ')) return <Typography key={i} variant="h4" sx={{ fontWeight: 700, mt: 1, mb: 2 }}>{line.slice(2)}</Typography>;
                    if (line.startsWith('- ') || line.startsWith('* ')) return <Typography key={i} variant="body1" sx={{ pl: 2, mb: 0.5 }}>{'• ' + line.slice(2)}</Typography>;
                    if (line.trim() === '') return <Box key={i} sx={{ height: 8 }} />;
                    return <Typography key={i} variant="body1" sx={{ mb: 0.5, lineHeight: 1.7 }}>{line}</Typography>;
                  })}
                </Box>
              </Paper>
            </Box>
          ) : (
            <TextField
              multiline
              fullWidth
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                  fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& textarea': { height: '100% !important' },
              }}
              placeholder="Escribe el contenido del archivo en formato Markdown..."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {fileContent.length} caracteres | {fileContent.split('\n').length} lineas
          </Typography>
          <Button onClick={handleCloseEditor}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !hasChanges}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeBasePage;
